import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Close as RejectIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  School as CoachIcon,
  Business as ClubIcon,
  AccountTree as InterclubIcon,
} from '@mui/icons-material';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { REGISTRATION_BASE_URL } from '../config/environment';

const ROLE_LABELS = {
  coach: { label: 'Coach', icon: <CoachIcon fontSize="small" />, color: 'warning' },
  club_admin: { label: 'Club Admin', icon: <ClubIcon fontSize="small" />, color: 'info' },
  interclub_admin: { label: 'Interclub Admin', icon: <InterclubIcon fontSize="small" />, color: 'success' },
};

export default function RoleApprovals() {
  const { getIdToken } = useFirebaseAuth();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [actionLoading, setActionLoading] = useState({});
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadPendingApprovals = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(`${REGISTRATION_BASE_URL}/role-approvals/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to load pending approvals');
      }

      const data = await response.json();
      setPendingApprovals(data.approvals || []);
    } catch (err) {
      console.error('Failed to load pending approvals:', err);
      setError(err.message || 'Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  const loadApprovalHistory = useCallback(async () => {
    try {
      const token = await getIdToken(true);
      if (!token) return;

      const response = await fetch(`${REGISTRATION_BASE_URL}/role-approvals/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setApprovalHistory(data.approvals || []);
      }
    } catch (err) {
      console.error('Failed to load approval history:', err);
    }
  }, [getIdToken]);

  useEffect(() => {
    loadPendingApprovals();
    loadApprovalHistory();
  }, [loadPendingApprovals, loadApprovalHistory]);

  const handleApprove = async (approval) => {
    setActionLoading((prev) => ({ ...prev, [approval.id]: true }));
    setError(null);

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(`${REGISTRATION_BASE_URL}/role-approvals/${approval.id}/approve`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to approve request');
      }

      setSuccess(`${approval.userName}'s ${ROLE_LABELS[approval.requestedRole]?.label || approval.requestedRole} role approved!`);
      setTimeout(() => setSuccess(null), 3000);

      // Refresh lists
      await Promise.all([loadPendingApprovals(), loadApprovalHistory()]);
    } catch (err) {
      console.error('Approval failed:', err);
      setError(err.message || 'Failed to approve request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [approval.id]: false }));
    }
  };

  const handleRejectClick = (approval) => {
    setRejectTarget(approval);
    setRejectReason('');
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectTarget) return;

    setActionLoading((prev) => ({ ...prev, [rejectTarget.id]: true }));
    setError(null);

    try {
      const token = await getIdToken(true);
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const response = await fetch(`${REGISTRATION_BASE_URL}/role-approvals/${rejectTarget.id}/reject`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ reason: rejectReason }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to reject request');
      }

      setSuccess(`${rejectTarget.userName}'s request has been rejected.`);
      setTimeout(() => setSuccess(null), 3000);

      setRejectDialogOpen(false);
      setRejectTarget(null);

      // Refresh lists
      await Promise.all([loadPendingApprovals(), loadApprovalHistory()]);
    } catch (err) {
      console.error('Rejection failed:', err);
      setError(err.message || 'Failed to reject request');
    } finally {
      setActionLoading((prev) => ({ ...prev, [rejectTarget?.id]: false }));
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'grey.200', borderRadius: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
          🛡️ Role Approvals
        </Typography>
        <IconButton onClick={() => { loadPendingApprovals(); loadApprovalHistory(); }} color="primary">
          <RefreshIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={`Pending (${pendingApprovals.length})`} />
        <Tab label="History" />
      </Tabs>

      {tab === 0 && (
        <>
          {pendingApprovals.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography color="text.secondary">No pending role approval requests.</Typography>
            </Paper>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Requested Role</TableCell>
                    <TableCell>Interclub</TableCell>
                    <TableCell>Club</TableCell>
                    <TableCell>Requested</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pendingApprovals.map((approval) => {
                    const roleInfo = ROLE_LABELS[approval.requestedRole] || {
                      label: approval.requestedRole,
                      icon: <PersonIcon fontSize="small" />,
                      color: 'default',
                    };
                    const isLoading = actionLoading[approval.id];

                    return (
                      <TableRow key={approval.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {approval.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {approval.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={roleInfo.icon}
                            label={roleInfo.label}
                            color={roleInfo.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>{approval.interclubName || '-'}</TableCell>
                        <TableCell>{approval.clubName || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(approval.requestedAt)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="Approve">
                              <IconButton
                                color="success"
                                onClick={() => handleApprove(approval)}
                                disabled={isLoading}
                                size="small"
                              >
                                {isLoading ? <CircularProgress size={20} /> : <ApproveIcon />}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                color="error"
                                onClick={() => handleRejectClick(approval)}
                                disabled={isLoading}
                                size="small"
                              >
                                <RejectIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {tab === 1 && (
        <>
          {approvalHistory.length === 0 ? (
            <Paper elevation={0} sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography color="text.secondary">No approval history yet.</Typography>
            </Paper>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Interclub</TableCell>
                    <TableCell>Reviewed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {approvalHistory.map((approval) => {
                    const roleInfo = ROLE_LABELS[approval.requestedRole] || {
                      label: approval.requestedRole,
                      icon: <PersonIcon fontSize="small" />,
                      color: 'default',
                    };

                    return (
                      <TableRow key={approval.id}>
                        <TableCell>
                          <Box>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                              {approval.userName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {approval.userEmail}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={roleInfo.icon}
                            label={roleInfo.label}
                            color={roleInfo.color}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={approval.status === 'approved' ? 'Approved' : 'Rejected'}
                            color={approval.status === 'approved' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{approval.interclubName || '-'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(approval.reviewedAt)}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Role Request</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Are you sure you want to reject {rejectTarget?.userName}'s request for the{' '}
              {ROLE_LABELS[rejectTarget?.requestedRole]?.label || rejectTarget?.requestedRole} role?
            </Typography>
            <TextField
              fullWidth
              label="Reason (optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              multiline
              rows={3}
              placeholder="Provide a reason for the rejection..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button
            onClick={handleRejectConfirm}
            variant="contained"
            color="error"
            disabled={actionLoading[rejectTarget?.id]}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
